import type { RecurringRevenueProductSummary, RecurringRevenueReport } from '@vy/types';
import { isNotNullOrUndefined, type ItemOf } from '@vy/utils';
import { extractMonthlyColumnKeys } from '../../utils/recurring-revenue-month-keys';

type ProductInfo = {
  productId: string;
  productName?: string;
  mrr: number;
  pricingModel: string;
  isPhase: boolean;
};

type MonthlyBreakdown = {
  totalMrr: number;
  movement: { type: string; amount: number } | null;
  products?: ProductInfo[];
};

// Flat, one-row-per-product shape (ENG-6239). The tree (customer → contract →
// product) was removed; each row carries the real customer/contract fields as
// plain columns plus a denormalized `productName`. `id` keeps row identity
// unique without clobbering the real `contractId`.
export type ExpandedRecurringRevenueRow = ItemOf<RecurringRevenueReport> & {
  id: string;
  productName: string;
  __productId?: string;
};

// Backend returns a flat list of contracts; we explode each contract into its
// products (from the monthly breakdown). Contracts with no product breakdown
// fall back to a single row so they aren't silently dropped. Customers are kept
// adjacent for readable default ordering.
export function expandRecurringRevenueRows(
  rows: RecurringRevenueReport,
): ExpandedRecurringRevenueRow[] {
  const grouped = groupByCustomerName(rows);
  const monthKeys = extractMonthlyColumnKeys(rows);
  const result: ExpandedRecurringRevenueRow[] = [];

  for (const [, contracts] of grouped) {
    for (const contract of contracts) {
      const productRows = buildProductRows(contract, monthKeys);

      if (productRows.length === 0) {
        result.push(buildContractFallbackRow(contract));
      } else {
        result.push(...productRows);
      }
    }
  }

  return result;
}

function buildProductRows(
  contract: ItemOf<RecurringRevenueReport>,
  monthKeys: string[],
): ExpandedRecurringRevenueRow[] {
  const products = collectProducts(contract, monthKeys);

  if (products.length === 0) {
    return [];
  }

  const summaryByProductId = indexProductSummaries(contract);
  const latestMonthKey = monthKeys.at(-1);

  return products.map(({ productId, productName }) => {
    const monthlyValues = monthKeys.reduce<Record<string, number>>((values, monthKey) => {
      const breakdown = (contract as Record<string, unknown>)[monthKey] as MonthlyBreakdown | undefined;
      const product = breakdown?.products?.find((p) => p.productId === productId);
      values[monthKey] = product?.mrr ?? 0;
      return values;
    }, {});

    // Real per-product MRR/ARR when the backend surfaces `productSummaries`
    // (their per-contract sum is exact); fall back to the latest visible
    // month's value before that data is available.
    const summary = summaryByProductId.get(productId);
    const currentMrrUsd =
      summary?.currentMrrUsd ?? (latestMonthKey ? (monthlyValues[latestMonthKey] ?? 0) : 0);
    const totalArrUsd = summary?.totalArrUsd ?? 0;

    return {
      ...contract,
      id: `${contract.contractId}-product-${productId}`,
      productName: productName ?? productId,
      __productId: productId,
      currentMrrUsd,
      totalArrUsd,
      currentMrrInOriginalCurrency: toOriginalCurrency(currentMrrUsd, contract.exchangeRate),
      ...monthlyValues,
    } as ExpandedRecurringRevenueRow;
  });
}

function indexProductSummaries(
  contract: ItemOf<RecurringRevenueReport>,
): Map<string, RecurringRevenueProductSummary> {
  const summaryByProductId = new Map<string, RecurringRevenueProductSummary>();
  for (const summary of contract.productSummaries ?? []) {
    summaryByProductId.set(summary.productId, summary);
  }
  return summaryByProductId;
}

function toOriginalCurrency(mrrUsd: number, exchangeRate: number | null): number {
  return isNotNullOrUndefined(exchangeRate) && exchangeRate !== 0 ? mrrUsd / exchangeRate : mrrUsd;
}

// A contract whose monthly breakdown carries no products still gets one row so
// it shows up in the flat view, using its contract-level values.
function buildContractFallbackRow(
  contract: ItemOf<RecurringRevenueReport>,
): ExpandedRecurringRevenueRow {
  return {
    ...contract,
    id: contract.contractId,
    productName: '--',
  } as ExpandedRecurringRevenueRow;
}

function collectProducts(
  contract: ItemOf<RecurringRevenueReport>,
  monthKeys: string[],
): { productId: string; productName?: string }[] {
  const productsMap = new Map<string, string | undefined>();

  for (const monthKey of monthKeys) {
    const breakdown = (contract as Record<string, unknown>)[monthKey] as MonthlyBreakdown | undefined;

    if (!breakdown?.products) {
      continue;
    }

    for (const product of breakdown.products) {
      if (!productsMap.has(product.productId)) {
        productsMap.set(product.productId, product.productName);
      }
    }
  }

  return Array.from(productsMap, ([productId, productName]) => ({ productId, productName }));
}

function groupByCustomerName(
  rows: RecurringRevenueReport,
): Map<string, ItemOf<RecurringRevenueReport>[]> {
  const map = new Map<string, ItemOf<RecurringRevenueReport>[]>();

  for (const row of rows) {
    const existing = map.get(row.customerName);

    if (isNotNullOrUndefined(existing)) {
      existing.push(row);
    } else {
      map.set(row.customerName, [row]);
    }
  }

  return map;
}
